// GET /pricing — the public tier catalog + the current promo (docs/API.md §3).
// The `pricing_tiers` table is the single source; the client never computes or
// hardcodes a price, and the server recomputes the amount again at order create.
//
// Anonymous on purpose: the catalog is public, and reading it must not depend on
// a session that may still be refreshing at mount.
//
// While new sales are switched off (`SALES_ENABLED=false`, the API default in
// every environment) this endpoint keeps the SAME wire shape but lists only the
// price-0 tiers with `promo: null` — it never answers 410. Callers detect that
// state by finding no paid tier in `tiers`, not by catching an error, and they
// all do it through `paidTiers` / `hasPaidTier` below so one rule decides
// whether a screen may offer a purchase.

import { apiGet } from "@/lib/api";
import type { PaidTierId, Pricing, PricingTier } from "@/lib/types";

export function getPricing(signal?: AbortSignal): Promise<Pricing> {
  return apiGet<Pricing>("/pricing", { anonymous: true, signal });
}

/** The purchasable tiers (two-tier model: Pro is the only one). */
export const PAID_TIERS: readonly PaidTierId[] = ["pro"];

export function isPaidTier(id: string): id is PaidTierId {
  return (PAID_TIERS as readonly string[]).includes(id);
}

/** The paid tiers this catalog actually offers. Empty = sales are off on this API. */
export function paidTiers(pricing: Pricing | null | undefined): PricingTier[] {
  return (pricing?.tiers ?? []).filter((t) => isPaidTier(t.id) && t.price > 0);
}

/** Whether this API is selling anything right now — the one gate for a purchase CTA. */
export function hasPaidTier(pricing: Pricing | null | undefined): boolean {
  return paidTiers(pricing).length > 0;
}
